import { useMemo } from 'react';
import { useJob } from '@/hooks/jobs/useJob';
import { useEstimate } from '@/hooks/estimates/useEstimate';
import { useJobPaymentSchedule } from './useJobPaymentSchedule';
import { useCreatePaymentSchedule } from './useCreatePaymentSchedule';
import { useUpdatePaymentSchedule } from './useUpdatePaymentSchedule';
import { useEstimateSettings } from '@/hooks/settings/useEstimateSettings';
import {
  type ScheduleRule,
  useAttachScheduleTemplate,
  useDeleteScheduleTemplate,
  usePatchScheduleTemplate,
  usePaymentScheduleTemplate,
  useTemplatePreview,
} from '@/hooks/payment-schedule-templates/usePaymentScheduleTemplate';
import type {
  AmountSpecKind,
  CreatePaymentScheduleRuleInput,
  PaymentAction,
  PaymentTrigger,
} from '@/types/paymentSchedule';
import type { EstimateStatus } from '@/types/estimate';
import { EDITABLE_STATUSES } from '@/components/organisms/estimates/support/estimate-action-visibility';

export type PaymentScheduleEntity = { type: 'job'; id: string } | { type: 'estimate'; id: string };

export interface UnifiedRule {
  trigger: PaymentTrigger;
  action: PaymentAction;
  amountSpec: { kind: AmountSpecKind; value?: number; daysAfter?: number; fixedDate?: string };
  autoCharge: boolean;
  paymentMethodId?: string | null;
  milestoneRef?: string | null;
  daysAfter?: number;
  fixedDate?: string;
}

export interface ExecutedPaymentScheduleRule extends UnifiedRule {
  id: string;
  firedAt: string;
}

export interface PaymentSchedulePreview {
  stages: Array<{
    position: number;
    label: string;
    triggerDescription: string;
    amount: { kind: string; amount: number | null };
    autoCharge: boolean;
    action: string;
  }>;
  grandTotal: number;
  consentRequired: boolean;
  previewText: string | null;
}

export interface PaymentScheduleEditorState {
  entity: PaymentScheduleEntity;
  contextHeader: { title: string; subtitle?: string };
  contactId: string | null;
  grandTotal: number;
  /** Whether the estimate carries a deposit clause (required for auto-charge, §4.4). */
  hasDepositClause: boolean;
  /** Dollars that clause collects — it counts against the plan's 100%. */
  depositAmount: number;
  /** Tenant's default schedule preset to pre-apply on a brand-new schedule (§8). */
  defaultPresetId: string | null;
  hasExistingSchedule: boolean;
  isLocked: boolean;
  lockedMessage?: string;
  isDisabled: boolean;
  scheduleRequired: boolean;
  initialRules: UnifiedRule[];
  executedRules: ExecutedPaymentScheduleRule[];
  initialPreviewText: string;
  preview: PaymentSchedulePreview | null;
  jobTiming: {
    scheduledStart: string | null;
    scheduledEnd: string | null;
    completedAt: string | null;
  };
  save: (rules: UnifiedRule[], previewText: string) => Promise<void>;
  remove: () => Promise<void>;
  isLoading: boolean;
  isSaving: boolean;
}

function resolveJobHeaderName(job: {
  jobNumber?: string;
  name?: string;
  jobType?: string | { name?: string };
}) {
  const name = (job.name ?? '').trim();
  const jobNumber = (job.jobNumber ?? '').trim();
  const jobTypeName = typeof job.jobType === 'string' ? job.jobType : job.jobType?.name;
  const generatedName = jobNumber ? `Job ${jobNumber}` : '';
  if (!name || name === jobNumber || name.toLowerCase() === generatedName.toLowerCase()) {
    return jobTypeName || 'Job';
  }
  if (jobNumber) {
    const withoutPrefix = name
      .replace(
        new RegExp(
          `^job\\s+${jobNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(?:[-:]\\s*)?`,
          'i',
        ),
        '',
      )
      .trim();
    if (withoutPrefix) return withoutPrefix;
  }
  return name;
}

function toUnifiedRule(rule: CreatePaymentScheduleRuleInput): UnifiedRule {
  return {
    trigger: rule.trigger,
    action: rule.action,
    amountSpec: rule.amountSpec,
    autoCharge: rule.action === 'AUTO_CHARGE' || Boolean(rule.autoCharge),
    paymentMethodId: rule.paymentMethodId ?? null,
    daysAfter: rule.daysAfter ?? rule.amountSpec.daysAfter,
    fixedDate: rule.fixedDate ?? rule.amountSpec.fixedDate,
    milestoneRef: rule.milestoneRef ?? null,
  };
}

function toJobRule(rule: UnifiedRule): CreatePaymentScheduleRuleInput {
  return {
    trigger: rule.trigger,
    action: rule.action,
    amountSpec: rule.amountSpec,
    autoCharge: rule.action === 'AUTO_CHARGE',
    paymentMethodId: rule.paymentMethodId ?? null,
    daysAfter: rule.daysAfter ?? rule.amountSpec.daysAfter,
    fixedDate: rule.fixedDate ?? rule.amountSpec.fixedDate,
    milestoneRef: rule.milestoneRef ?? null,
  };
}

export function toEstimateRule(rule: UnifiedRule): ScheduleRule {
  return {
    trigger: rule.trigger,
    action: rule.action,
    amountSpec: {
      ...rule.amountSpec,
      daysAfter: rule.daysAfter ?? rule.amountSpec.daysAfter,
      fixedDate: rule.fixedDate ?? rule.amountSpec.fixedDate,
    },
    autoCharge: rule.action === 'AUTO_CHARGE',
    milestoneRef: rule.milestoneRef ?? undefined,
  };
}

function formatEstimateStatus(status: EstimateStatus): string {
  return status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function usePaymentScheduleEditor(
  entity: PaymentScheduleEntity,
): PaymentScheduleEditorState {
  const jobId = entity.type === 'job' ? entity.id : '';
  const estimateId = entity.type === 'estimate' ? entity.id : '';

  const jobQuery = useJob(jobId);
  const jobScheduleQuery = useJobPaymentSchedule(jobId || undefined);
  const jobSchedule = jobScheduleQuery.data ?? null;
  const createJobSchedule = useCreatePaymentSchedule();
  const updateJobSchedule = useUpdatePaymentSchedule(jobSchedule?.id ?? 'none');

  const estimateQuery = useEstimate(estimateId);
  const estimateSettingsQuery = useEstimateSettings({ enabled: entity.type === 'estimate' });
  const templateQuery = usePaymentScheduleTemplate(estimateId || undefined);
  const previewQuery = useTemplatePreview(estimateId || undefined, { audience: 'staff' });
  const template = templateQuery.data ?? null;
  const attachTemplate = useAttachScheduleTemplate(estimateId || 'none');
  const patchTemplate = usePatchScheduleTemplate(estimateId || 'none');
  const deleteTemplate = useDeleteScheduleTemplate(estimateId || 'none');

  const jobInitialRules = useMemo<UnifiedRule[]>(() => {
    if (!jobSchedule) return [];
    return jobSchedule.rules
      .filter((rule) => rule.firedAt === null)
      .sort((a, b) => a.position - b.position)
      .map((rule) =>
        toUnifiedRule({
          trigger: rule.trigger,
          action: rule.action,
          amountSpec: rule.amountSpec,
          autoCharge: rule.autoCharge,
          paymentMethodId: rule.paymentMethodId,
          daysAfter: rule.amountSpec.daysAfter,
          fixedDate: rule.amountSpec.fixedDate,
          milestoneRef: rule.milestoneRef,
        }),
      );
  }, [jobSchedule]);

  const jobExecutedRules = useMemo<ExecutedPaymentScheduleRule[]>(() => {
    if (!jobSchedule) return [];
    return jobSchedule.rules
      .filter((rule): rule is typeof rule & { firedAt: string } => rule.firedAt !== null)
      .sort((a, b) => a.position - b.position)
      .map((rule) => ({
        id: rule.id,
        firedAt: rule.firedAt,
        ...toUnifiedRule({
          trigger: rule.trigger,
          action: rule.action,
          amountSpec: rule.amountSpec,
          autoCharge: rule.autoCharge,
          paymentMethodId: rule.paymentMethodId,
          daysAfter: rule.amountSpec.daysAfter,
          fixedDate: rule.amountSpec.fixedDate,
          milestoneRef: rule.milestoneRef,
        }),
      }));
  }, [jobSchedule]);

  const estimateInitialRules = useMemo<UnifiedRule[]>(() => {
    if (!template) return [];
    return [...template.rules]
      .sort((a, b) => a.position - b.position)
      .map((rule) => ({
        trigger: rule.trigger,
        action: rule.action,
        amountSpec: rule.amountSpec,
        autoCharge: rule.action === 'AUTO_CHARGE' || Boolean(rule.autoCharge),
        milestoneRef: rule.milestoneRef ?? null,
        daysAfter: rule.amountSpec.daysAfter,
        fixedDate: rule.amountSpec.fixedDate,
      }));
  }, [template]);

  if (entity.type === 'job') {
    const job = jobQuery.data;
    const jobStatus = String(job?.statusPhase ?? job?.status ?? '').toUpperCase();
    const jobIsLocked = ['COMPLETED', 'CANCELLED', 'CLOSED', 'ARCHIVED'].includes(jobStatus);
    const scheduleIsLocked = jobSchedule?.status === 'COMPLETED';
    const isLocked = jobIsLocked || scheduleIsLocked;
    return {
      entity,
      contextHeader: {
        title: 'Edit payment schedule',
        subtitle: job
          ? [job.jobNumber, resolveJobHeaderName(job)].filter(Boolean).join(' · ')
          : undefined,
      },
      contactId: job?.contactId ?? null,
      grandTotal: job?.grandTotal ?? 0,
      hasDepositClause: false,
      depositAmount: 0,
      defaultPresetId: null,
      hasExistingSchedule: Boolean(jobSchedule),
      isLocked,
      lockedMessage: scheduleIsLocked
        ? 'This payment schedule is complete. Its executed rules are shown below and cannot be changed.'
        : jobIsLocked
          ? `This job is ${jobStatus.toLowerCase().replaceAll('_', ' ')}. Its payment schedule is read-only.`
          : undefined,
      isDisabled: false,
      scheduleRequired: false,
      initialRules: jobInitialRules,
      executedRules: jobExecutedRules,
      initialPreviewText: '',
      preview: null,
      jobTiming: {
        scheduledStart: job?.scheduledStart ?? null,
        scheduledEnd: job?.scheduledEnd ?? null,
        completedAt: job?.completedAt ?? null,
      },
      save: async (rules) => {
        const jobRules = rules.map(toJobRule);
        if (jobSchedule) await updateJobSchedule.mutateAsync({ rules: jobRules });
        else await createJobSchedule.mutateAsync({ jobId: entity.id, rules: jobRules });
      },
      remove: async () => {},
      isLoading: jobQuery.isLoading || jobScheduleQuery.isLoading,
      isSaving: createJobSchedule.isPending || updateJobSchedule.isPending,
    };
  }

  const estimate = estimateQuery.data;
  const tenantDefaults =
    ((estimateSettingsQuery.data as unknown as { paymentSchedule?: Record<string, unknown> })
      ?.paymentSchedule as Record<string, unknown> | undefined) ?? {};
  const requireScheduleAboveAmount =
    typeof tenantDefaults.requireScheduleAboveAmount === 'number'
      ? tenantDefaults.requireScheduleAboveAmount
      : 0;
  const allowPaymentSchedules =
    tenantDefaults.allowPaymentSchedules === undefined
      ? true
      : Boolean(tenantDefaults.allowPaymentSchedules);
  // NOT-229 §8: tenant's default preset to pre-apply on a brand-new schedule.
  const defaultPresetId =
    typeof tenantDefaults.defaultPresetId === 'string' ? tenantDefaults.defaultPresetId : null;
  const scheduleRequired =
    allowPaymentSchedules &&
    requireScheduleAboveAmount > 0 &&
    typeof estimate?.grandTotal === 'number' &&
    estimate.grandTotal > requireScheduleAboveAmount;
  const estimateStatus = estimate?.status;
  const estimateIsLocked = Boolean(estimateStatus && !EDITABLE_STATUSES.includes(estimateStatus));
  const templateIsLocked = Boolean(template && template.status !== 'DRAFT');
  const isLocked = estimateIsLocked || templateIsLocked;
  const lockedMessage = estimateIsLocked
    ? `This estimate is ${formatEstimateStatus(estimateStatus as EstimateStatus)}. Payment schedules can only be edited while the estimate is in Draft, Review, or Revised status.${
        template ? ' The existing schedule is shown below.' : ''
      }`
    : "Schedule is locked. Edits go on the Job's Payment Schedule once work begins.";

  return {
    entity,
    contextHeader: {
      title: 'Edit payment schedule',
      subtitle: estimate
        ? [estimate.estimateNumber, estimate.customer?.name].filter(Boolean).join(' · ')
        : undefined,
    },
    contactId: estimate?.contactId ?? null,
    grandTotal: estimate?.grandTotal ?? 0,
    hasDepositClause: Boolean(estimate?.depositRequired),
    depositAmount: Number(estimate?.depositAmount ?? 0),
    defaultPresetId,
    hasExistingSchedule: Boolean(template),
    isLocked,
    lockedMessage,
    isDisabled: !allowPaymentSchedules && !template,
    scheduleRequired,
    initialRules: estimateInitialRules,
    executedRules: [],
    initialPreviewText: template?.previewText ?? '',
    preview:
      previewQuery.data != null && (previewQuery.data as { stages?: unknown[] }).stages?.length
        ? (previewQuery.data as PaymentSchedulePreview)
        : null,
    jobTiming: {
      scheduledStart: null,
      scheduledEnd: null,
      completedAt: null,
    },
    save: async (rules, previewText) => {
      const estimateRules = rules.map(toEstimateRule);
      if (template) await patchTemplate.mutateAsync({ rules: estimateRules, previewText });
      else await attachTemplate.mutateAsync({ rules: estimateRules, previewText });
    },
    remove: async () => {
      if (template) await deleteTemplate.mutateAsync();
    },
    isLoading:
      estimateQuery.isLoading || templateQuery.isLoading || estimateSettingsQuery.isLoading,
    isSaving: attachTemplate.isPending || patchTemplate.isPending || deleteTemplate.isPending,
  };
}
